/**
 * Equipment Rental Marketplace
 * Professional construction equipment rental with calendar booking system
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Alert,
  Skeleton,
  Badge,
  Tooltip,
  Avatar,
  LinearProgress,
  Calendar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Event,
  Schedule,
  Build,
  Engineering,
  LocalShipping,
  Security,
  Support,
  CheckCircle,
  Cancel,
  Info,
  Verified,
  LocationOn,
  Phone,
  WhatsApp,
  CalendarToday,
  AccessTime,
  Person,
  Settings,
  Warning,
  Star,
  Favorite,
  FavoriteBorder,
  ExpandMore
} from '@mui/icons-material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import MarketplaceLayout from '../../components/marketplace/MarketplaceLayout';
import { formatCurrency, formatDate, formatDateTime } from '../../utils/helpers';
import DialogCloseButton from '../../components/common/DialogCloseButton';

// Mock equipment data
const mockEquipmentData = [
  {
    id: 1,
    name: '320D2 Excavator',
    description: 'Caterpillar 320D2 hydraulic excavator perfect for medium to large construction projects.',
    dailyRate: 450,
    weeklyRate: 2800,
    monthlyRate: 10500,
    images: ['/api/placeholder/400/300', '/api/placeholder/400/300'],
    vendor: {
      id: 1,
      name: 'Heavy Plant Hire Zimbabwe',
      logo: '/api/placeholder/50/50',
      rating: 4.9,
      reviewCount: 187,
      isVerified: true,
      location: 'Harare',
      phone: '+263 4 123456',
      whatsapp: '+263 77 123456'
    },
    category: 'Excavators',
    specifications: {
      'Operating Weight': '22,000 kg',
      'Engine Power': '122 kW (164 hp)',
      'Bucket Capacity': '1.0 m³',
      'Max Digging Depth': '6.7 m',
      'Max Reach': '10.1 m'
    },
    features: ['GPS Tracking', 'Air Conditioned Cabin', 'Quick Coupler', '24/7 Support'],
    operatorIncluded: true,
    operatorRate: 80, // per day
    maintenanceIncluded: true,
    fuelIncluded: false,
    deliveryRadius: 50,
    deliveryCost: 150,
    setupCost: 200,
    minimumRental: 1, // days
    maximumRental: 365,
    availability: {
      '2024-01-15': 'available',
      '2024-01-16': 'available',
      '2024-01-17': 'booked',
      '2024-01-18': 'maintenance',
      '2024-01-19': 'available'
    },
    upcomingMaintenance: '2024-01-25',
    condition: 'Excellent',
    year: 2022,
    rating: 4.9,
    reviewCount: 43,
    isPopular: true,
    insuranceRequired: true,
    minimumInsurance: 50000,
    certificationRequired: ['Heavy Equipment License', 'Safety Certificate'],
    safetyFeatures: ['ROPS/FOPS Certified', 'Backup Camera', 'Warning Lights']
  },
  {
    id: 2,
    name: 'Mobile Tower Crane - 50 Ton',
    description: 'Liebherr mobile tower crane ideal for high-rise construction projects.',
    dailyRate: 850,
    weeklyRate: 5200,
    monthlyRate: 19500,
    images: ['/api/placeholder/400/300', '/api/placeholder/400/300'],
    vendor: {
      id: 2,
      name: 'Crane Masters Zimbabwe',
      logo: '/api/placeholder/50/50',
      rating: 4.8,
      reviewCount: 156,
      isVerified: true,
      location: 'Bulawayo',
      phone: '+263 9 234567',
      whatsapp: '+263 77 234567'
    },
    category: 'Cranes',
    specifications: {
      'Max Lifting Capacity': '50 tons',
      'Max Hook Height': '60 m',
      'Max Working Radius': '50 m',
      'Boom Length': '55 m',
      'Counterweight': '45 tons'
    },
    features: ['Certified Operator', 'Load Monitoring System', 'Anti-Collision System', 'Remote Control'],
    operatorIncluded: true,
    operatorRate: 150, // per day - specialized operator
    maintenanceIncluded: true,
    fuelIncluded: false,
    deliveryRadius: 100,
    deliveryCost: 500,
    setupCost: 1500,
    minimumRental: 3, // days
    maximumRental: 180,
    availability: {
      '2024-01-15': 'available',
      '2024-01-16': 'available',
      '2024-01-17': 'available'
    },
    condition: 'Excellent',
    year: 2021,
    rating: 4.8,
    reviewCount: 28,
    isFeatured: true,
    insuranceRequired: true,
    minimumInsurance: 500000,
    certificationRequired: ['Crane Operator License', 'Rigging Certificate', 'Safety Manager Certificate'],
    safetyFeatures: ['Load Block', 'Overload Protection', 'Emergency Stop', 'Wind Speed Monitor']
  },
  {
    id: 3,
    name: 'Concrete Mixer - 6m³',
    description: 'Transit mixer truck for concrete delivery and on-site mixing.',
    dailyRate: 320,
    weeklyRate: 1950,
    monthlyRate: 7200,
    images: ['/api/placeholder/400/300'],
    vendor: {
      id: 3,
      name: 'Concrete Solutions Ltd',
      logo: '/api/placeholder/50/50',
      rating: 4.6,
      reviewCount: 93,
      isVerified: true,
      location: 'Gweru',
      phone: '+263 54 345678',
      whatsapp: '+263 77 345678'
    },
    category: 'Mixers',
    specifications: {
      'Drum Capacity': '6 m³',
      'Output per Hour': '20-30 m³',
      'Engine Power': '280 HP',
      'Max Travel Speed': '80 km/h',
      'Discharge Height': '4.2 m'
    },
    features: ['Auto Weighing', 'Water System', 'Hydraulic Drive', 'GPS Tracking'],
    operatorIncluded: true,
    operatorRate: 60,
    maintenanceIncluded: true,
    fuelIncluded: true,
    deliveryRadius: 75,
    deliveryCost: 100,
    setupCost: 0,
    minimumRental: 1,
    maximumRental: 30,
    availability: {},
    condition: 'Good',
    year: 2020,
    rating: 4.6,
    reviewCount: 67,
    isNew: false,
    insuranceRequired: false,
    safetyFeatures: ['Backup Alarm', 'Safety Lights', 'Emergency Stop']
  },
  {
    id: 4,
    name: 'Generator - 100 kVA',
    description: 'Diesel generator for construction site power supply.',
    dailyRate: 180,
    weeklyRate: 1100,
    monthlyRate: 4200,
    images: ['/api/placeholder/400/300'],
    vendor: {
      id: 4,
      name: 'Power Solutions Zimbabwe',
      logo: '/api/placeholder/50/50',
      rating: 4.7,
      reviewCount: 124,
      isVerified: true,
      location: 'Harare',
      phone: '+263 4 456789',
      whatsapp: '+263 77 456789'
    },
    category: 'Generators',
    specifications: {
      'Power Output': '100 kVA / 80 kW',
      'Engine Type': 'Diesel',
      'Fuel Tank Capacity': '400 L',
      'Runtime': '10 hours at full load',
      'Noise Level': '75 dB at 7m'
    },
    features: ['Auto Start/Stop', 'Remote Monitoring', 'Weather Protection', '24/7 Support'],
    operatorIncluded: false,
    operatorRate: 0,
    maintenanceIncluded: true,
    fuelIncluded: false,
    deliveryRadius: 100,
    deliveryCost: 80,
    setupCost: 150,
    minimumRental: 1,
    maximumRental: 90,
    availability: {},
    condition: 'Excellent',
    year: 2023,
    rating: 4.7,
    reviewCount: 35,
    isNew: true,
    insuranceRequired: false,
    safetyFeatures: ['Emergency Stop', 'Overload Protection', 'Low Oil Pressure Shutdown']
  }
];

const EquipmentMarketplace = () => {
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingDialog, setBookingDialog] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [bookingDetails, setBookingDetails] = useState({
    startDate: null,
    endDate: null,
    startTime: null,
    endTime: null,
    rentalPeriod: 'daily',
    includeOperator: true,
    deliveryRequired: true,
    specialRequirements: '',
    projectDetails: '',
    insuranceConfirmation: false
  });
  const [bookingStep, setBookingStep] = useState(0);
  const [availabilityData, setAvailabilityData] = useState({});

  // Load equipment data
  useEffect(() => {
    const loadEquipment = async () => {
      setLoading(true);
      setTimeout(() => {
        // TODO: Replace with real API call to /api/marketplace/equipment
        setEquipment([]); // Empty - no mock data
        setLoading(false);
      }, 500);
    };

    loadEquipment();
  }, []);

  // Calculate rental cost
  const calculateRentalCost = (equipment, details) => {
    if (!details.startDate || !details.endDate) return 0;
    
    const days = Math.ceil((details.endDate - details.startDate) / (1000 * 60 * 60 * 24)) || 1;
    let rate = equipment.dailyRate;
    
    if (details.rentalPeriod === 'weekly' && days >= 7) {
      rate = equipment.weeklyRate / 7;
    } else if (details.rentalPeriod === 'monthly' && days >= 30) {
      rate = equipment.monthlyRate / 30;
    }

    let totalCost = rate * days;
    
    if (details.includeOperator && equipment.operatorIncluded) {
      totalCost += equipment.operatorRate * days;
    }
    
    if (details.deliveryRequired) {
      totalCost += equipment.deliveryCost;
      totalCost += equipment.setupCost || 0;
    }

    return totalCost;
  };

  // Handle booking
  const handleBookEquipment = (equipmentItem) => {
    setSelectedEquipment(equipmentItem);
    setBookingDetails({
      ...bookingDetails,
      includeOperator: equipmentItem.operatorIncluded,
      insuranceConfirmation: !equipmentItem.insuranceRequired
    });
    setBookingStep(0);
    setBookingDialog(true);
  };

  // Booking Dialog Steps
  const BookingDialog = () => {
    const steps = ['Date & Time', 'Options', 'Details', 'Confirmation'];

    const renderStepContent = () => {
      switch (bookingStep) {
        case 0:
          return (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Select Rental Period
                  </Typography>
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>Rental Period</InputLabel>
                    <Select
                      value={bookingDetails.rentalPeriod}
                      label="Rental Period"
                      onChange={(e) => setBookingDetails({...bookingDetails, rentalPeriod: e.target.value})}
                    >
                      <MenuItem value="daily">Daily Rental</MenuItem>
                      <MenuItem value="weekly">Weekly Rental</MenuItem>
                      <MenuItem value="monthly">Monthly Rental</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={6}>
                  <DatePicker
                    label="Start Date"
                    value={bookingDetails.startDate}
                    onChange={(date) => setBookingDetails({...bookingDetails, startDate: date})}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                    minDate={new Date()}
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <DatePicker
                    label="End Date"
                    value={bookingDetails.endDate}
                    onChange={(date) => setBookingDetails({...bookingDetails, endDate: date})}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                    minDate={bookingDetails.startDate || new Date()}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TimePicker
                    label="Start Time"
                    value={bookingDetails.startTime}
                    onChange={(time) => setBookingDetails({...bookingDetails, startTime: time})}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TimePicker
                    label="End Time"
                    value={bookingDetails.endTime}
                    onChange={(time) => setBookingDetails({...bookingDetails, endTime: time})}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>

                {/* Availability Calendar Preview */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Equipment Availability
                  </Typography>
                  <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      📅 Available dates shown in green, booked dates in red, maintenance in orange
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </LocalizationProvider>
          );

        case 1:
          return (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Rental Options
                </Typography>
              </Grid>

              {selectedEquipment?.operatorIncluded && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={bookingDetails.includeOperator}
                        onChange={(e) => setBookingDetails({...bookingDetails, includeOperator: e.target.checked})}
                      />
                    }
                    label={
                      <Box>
                        <Typography>Include Certified Operator</Typography>
                        <Typography variant="caption" color="text.secondary">
                          +{formatCurrency(selectedEquipment.operatorRate)}/day
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={bookingDetails.deliveryRequired}
                      onChange={(e) => setBookingDetails({...bookingDetails, deliveryRequired: e.target.checked})}
                    />
                  }
                  label={
                    <Box>
                      <Typography>Delivery & Setup Required</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Delivery: {formatCurrency(selectedEquipment?.deliveryCost || 0)} | 
                        Setup: {formatCurrency(selectedEquipment?.setupCost || 0)}
                      </Typography>
                    </Box>
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Special Requirements"
                  value={bookingDetails.specialRequirements}
                  onChange={(e) => setBookingDetails({...bookingDetails, specialRequirements: e.target.value})}
                  placeholder="Any special requirements, site conditions, or additional equipment needed..."
                />
              </Grid>
            </Grid>
          );

        case 2:
          return (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Project Details
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Project Description"
                  value={bookingDetails.projectDetails}
                  onChange={(e) => setBookingDetails({...bookingDetails, projectDetails: e.target.value})}
                  placeholder="Describe your project, site location, and how the equipment will be used..."
                  required
                />
              </Grid>

              {selectedEquipment?.insuranceRequired && (
                <Grid item xs={12}>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    This equipment requires minimum insurance coverage of {formatCurrency(selectedEquipment.minimumInsurance)}
                  </Alert>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={bookingDetails.insuranceConfirmation}
                        onChange={(e) => setBookingDetails({...bookingDetails, insuranceConfirmation: e.target.checked})}
                      />
                    }
                    label="I confirm adequate insurance coverage is in place"
                    required
                  />
                </Grid>
              )}

              {selectedEquipment?.certificationRequired?.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Required Certifications:
                  </Typography>
                  <List dense>
                    {selectedEquipment.certificationRequired.map((cert, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <CheckCircle color="warning" />
                        </ListItemIcon>
                        <ListItemText primary={cert} />
                      </ListItem>
                    ))}
                  </List>
                  <Typography variant="caption" color="text.secondary">
                    Please ensure all operators have the required certifications before equipment use.
                  </Typography>
                </Grid>
              )}
            </Grid>
          );

        case 3:
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                Booking Confirmation
              </Typography>
              
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {selectedEquipment?.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedEquipment?.vendor.name}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Start Date</Typography>
                      <Typography variant="body1">
                        {bookingDetails.startDate ? formatDateTime(bookingDetails.startDate) : 'Not set'}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">End Date</Typography>
                      <Typography variant="body1">
                        {bookingDetails.endDate ? formatDateTime(bookingDetails.endDate) : 'Not set'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom>Cost Breakdown</Typography>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Equipment Rental</Typography>
                        <Typography variant="body2">
                          {formatCurrency(calculateRentalCost(selectedEquipment || {}, bookingDetails) - 
                            (bookingDetails.includeOperator ? (selectedEquipment?.operatorRate || 0) * 
                            Math.ceil((bookingDetails.endDate - bookingDetails.startDate) / (1000 * 60 * 60 * 24) || 1) : 0) -
                            (bookingDetails.deliveryRequired ? (selectedEquipment?.deliveryCost || 0) + (selectedEquipment?.setupCost || 0) : 0))}
                        </Typography>
                      </Box>

                      {bookingDetails.includeOperator && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">Operator</Typography>
                          <Typography variant="body2">
                            {formatCurrency((selectedEquipment?.operatorRate || 0) * 
                            Math.ceil((bookingDetails.endDate - bookingDetails.startDate) / (1000 * 60 * 60 * 24) || 1))}
                          </Typography>
                        </Box>
                      )}

                      {bookingDetails.deliveryRequired && (
                        <>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Delivery</Typography>
                            <Typography variant="body2">
                              {formatCurrency(selectedEquipment?.deliveryCost || 0)}
                            </Typography>
                          </Box>
                          {(selectedEquipment?.setupCost || 0) > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2">Setup</Typography>
                              <Typography variant="body2">
                                {formatCurrency(selectedEquipment?.setupCost || 0)}
                              </Typography>
                            </Box>
                          )}
                        </>
                      )}

                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="h6">Total</Typography>
                        <Typography variant="h6" color="primary">
                          {formatCurrency(calculateRentalCost(selectedEquipment || {}, bookingDetails))}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Alert severity="info">
                Your booking request will be sent to the equipment provider for confirmation. 
                You will receive a response within 2 hours during business hours.
              </Alert>
            </Box>
          );

        default:
          return null;
      }
    };

    return (
      <Dialog
        open={bookingDialog}
        onClose={() => setBookingDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogCloseButton onClick={() => setBookingDialog(false)} />
        <DialogTitle>
          Book Equipment - {selectedEquipment?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <LinearProgress 
              variant="determinate" 
              value={(bookingStep + 1) / steps.length * 100} 
              sx={{ mb: 2 }}
            />
            <Typography variant="caption" color="text.secondary">
              Step {bookingStep + 1} of {steps.length}: {steps[bookingStep]}
            </Typography>
          </Box>

          {renderStepContent()}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setBookingDialog(false)}
            disabled={bookingStep === 3}
          >
            Cancel
          </Button>
          {bookingStep > 0 && (
            <Button onClick={() => setBookingStep(bookingStep - 1)}>
              Back
            </Button>
          )}
          {bookingStep < 3 ? (
            <Button 
              variant="contained"
              onClick={() => setBookingStep(bookingStep + 1)}
              disabled={
                (bookingStep === 0 && (!bookingDetails.startDate || !bookingDetails.endDate)) ||
                (bookingStep === 2 && !bookingDetails.projectDetails) ||
                (bookingStep === 2 && selectedEquipment?.insuranceRequired && !bookingDetails.insuranceConfirmation)
              }
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              onClick={() => {
                // Handle booking submission
                alert('Booking request submitted successfully!');
                setBookingDialog(false);
              }}
            >
              Confirm Booking
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  // Equipment Card Component
  const EquipmentCard = ({ equipmentItem }) => (
    <Card sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
      }
    }}>
      {/* Image and badges */}
      <Box sx={{ position: 'relative' }}>
        <CardMedia
          component="img"
          height="200"
          image={equipmentItem.images[0]}
          alt={equipmentItem.name}
        />
        
        <Box sx={{ position: 'absolute', top: 8, left: 8, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {equipmentItem.isPopular && <Chip label="Popular" size="small" color="warning" />}
          {equipmentItem.isNew && <Chip label="New" size="small" color="success" />}
          {equipmentItem.isFeatured && <Chip label="Featured" size="small" color="primary" />}
          <Chip 
            label={equipmentItem.condition} 
            size="small" 
            color={equipmentItem.condition === 'Excellent' ? 'success' : 'default'} 
          />
        </Box>

        <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
          <IconButton size="small" sx={{ bgcolor: 'rgba(255,255,255,0.9)' }}>
            <FavoriteBorder />
          </IconButton>
        </Box>
      </Box>

      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        {/* Vendor info */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar src={equipmentItem.vendor.logo} sx={{ width: 24, height: 24, mr: 1 }} />
            <Typography variant="caption" color="text.secondary">
              {equipmentItem.vendor.name}
            </Typography>
            {equipmentItem.vendor.isVerified && <Verified color="primary" sx={{ ml: 0.5, fontSize: 12 }} />}
          </Box>
          <Typography variant="caption" color="text.secondary">
            {equipmentItem.year}
          </Typography>
        </Box>

        {/* Equipment name and category */}
        <Typography variant="h6" component="h3" sx={{ mb: 1, fontWeight: 600, lineHeight: 1.3 }}>
          {equipmentItem.name}
        </Typography>

        <Chip label={equipmentItem.category} size="small" sx={{ mb: 2 }} />

        {/* Pricing */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" color="primary" sx={{ fontWeight: 700, mb: 0.5 }}>
            {formatCurrency(equipmentItem.dailyRate)}/day
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Weekly: {formatCurrency(equipmentItem.weeklyRate)} • Monthly: {formatCurrency(equipmentItem.monthlyRate)}
          </Typography>
        </Box>

        {/* Key features */}
        <Box sx={{ mb: 2 }}>
          {equipmentItem.operatorIncluded && (
            <Chip 
              icon={<Person />} 
              label={`Operator +${formatCurrency(equipmentItem.operatorRate)}/day`} 
              size="small" 
              variant="outlined" 
              sx={{ mr: 0.5, mb: 0.5 }} 
            />
          )}
          {equipmentItem.maintenanceIncluded && (
            <Chip 
              icon={<Settings />} 
              label="Maintenance Incl." 
              size="small" 
              color="success"
              variant="outlined" 
              sx={{ mr: 0.5, mb: 0.5 }} 
            />
          )}
          {equipmentItem.insuranceRequired && (
            <Chip 
              icon={<Security />} 
              label="Insurance Req." 
              size="small" 
              color="warning"
              variant="outlined" 
              sx={{ mr: 0.5, mb: 0.5 }} 
            />
          )}
        </Box>

        {/* Location and delivery */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            {equipmentItem.vendor.location} • {equipmentItem.deliveryRadius}km radius
          </Typography>
        </Box>

        {/* Top specifications */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {Object.entries(equipmentItem.specifications).slice(0, 2).map(([key, value], index) => (
              <span key={index}>
                {key}: {value}
                {index < 1 && ' • '}
              </span>
            ))}
          </Typography>
        </Box>
      </CardContent>

      {/* Actions */}
      <Box sx={{ p: 2, pt: 0 }}>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Info />}
              size="small"
              onClick={() => {/* View details */}}
            >
              Details
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<Event />}
              onClick={() => handleBookEquipment(equipmentItem)}
              size="small"
            >
              Book Now
            </Button>
          </Grid>
        </Grid>

        {/* Quick contact */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1 }}>
          <IconButton size="small" color="success">
            <WhatsApp />
          </IconButton>
          <IconButton size="small" color="primary">
            <Phone />
          </IconButton>
        </Box>
      </Box>
    </Card>
  );

  return (
    <>
      <MarketplaceLayout
        category="equipment"
        title="Construction Equipment Rental"
        description="Professional construction equipment from verified rental companies. Daily, weekly, and monthly rates with certified operators."
        data={equipment}
        loading={loading}
        totalResults={equipment.length}
        customActions={[
          {
            icon: <CalendarToday />,
            name: 'My Bookings',
            action: () => console.log('View bookings')
          }
        ]}
      >
        {/* Equipment Grid */}
        {loading ? (
          <Grid container spacing={3}>
            {[...Array(8)].map((_, index) => (
              <Grid item key={index} xs={12} sm={6} md={4} lg={3}>
                <Skeleton variant="rectangular" height={450} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={3}>
            {equipment.map((equipmentItem) => (
              <Grid item key={equipmentItem.id} xs={12} sm={6} md={6} lg={4}>
                <EquipmentCard equipmentItem={equipmentItem} />
              </Grid>
            ))}
          </Grid>
        )}
      </MarketplaceLayout>

      {/* Booking Dialog */}
      <BookingDialog />
    </>
  );
};

export default EquipmentMarketplace;